/**
 * Settings › Client portal (docs/PLAN-CUSTOMER-PORTAL-2026-09-30.md).
 *
 * What this organisation's clients see at /portal/: its name, its logo, its
 * colours, and who to contact. The portal has always read these; nothing in
 * the console could set them (only the email sender identity, next door in
 * Mail). The logo is uploaded and kept in CosMon (migration 071),
 * PNG/JPEG/WebP only.
 *
 * Three colours (077). The portal keeps text readable whatever is picked, by
 * not using a colour as text where it would not clear 4.5:1 — and this screen
 * says where that will happen, because a colour saved and silently not used
 * is the thing migration 051 warns about.
 */

import { BRANDING_CHANGED } from './console-brand.ts';
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from './api.ts';
import { Banner, Button, Field, Loading, Section } from './components/index.ts';

/** Whether the portal will use a colour as text on each theme. */
interface Readability {
  light: boolean;
  dark: boolean;
}

interface PortalBranding {
  productName: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  colorPrimary: string | null;
  colorAccent: string | null;
  colorHeader: string | null;
  readability: { primary: Readability | null; accent: Readability | null };
  logoUrl: string | null;
  canChange: boolean;
}

const HEX = /^#[0-9a-fA-F]{6}$/;

/** What the portal will do with a saved colour as text, in words. */
function readabilityNote(r: Readability | null): string | null {
  if (r === null) return null;
  if (r.light && r.dark) return 'Readable as text on both the light and dark portal.';
  if (!r.light && !r.dark) return 'Too low-contrast for text on either theme: used for fills only, text keeps the portal’s own colour.';
  return `Readable as text on the ${r.light ? 'light' : 'dark'} portal only; on the ${r.light ? 'dark' : 'light'} one text keeps the portal’s own colour.`;
}

function ColourField({
  id, label, hint, value, fallback, disabled, note, onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: string;
  fallback: string;
  disabled: boolean;
  note: string | null;
  onChange: (next: string) => void;
}): React.JSX.Element {
  return (
    <Field id={id} label={label} hint={hint}>
      <div className="row-tight">
        <input
          type="color"
          aria-label={`Pick ${label.toLowerCase()}`}
          value={HEX.test(value) ? value : fallback}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        />
        <input id={id} value={value} disabled={disabled} placeholder="#1f6feb" maxLength={7} onChange={(e) => onChange(e.target.value)} />
      </div>
      {note !== null && <p className="field-hint muted small">{note}</p>}
    </Field>
  );
}

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

const TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export function PortalBrandingView(): React.JSX.Element {
  const [state, setState] = useState<PortalBranding | null>(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [colour, setColour] = useState('');
  const [accent, setAccent] = useState('');
  const [header, setHeader] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api.get<PortalBranding>('/api/tenant/portal-branding');
      setState(next);
      setName(next.productName ?? '');
      setEmail(next.supportEmail ?? '');
      setPhone(next.supportPhone ?? '');
      setColour(next.colorPrimary ?? '');
      setAccent(next.colorAccent ?? '');
      setHeader(next.colorHeader ?? '');
    } catch (err) {
      setError(failure(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && state === null) return <Banner tone="fault">{error}</Banner>;
  if (state === null) return <Loading rows={4} cols={1} label="Loading the client portal settings" />;

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      await api.put('/api/tenant/portal-branding', {
        productName: name,
        supportEmail: email,
        supportPhone: phone,
        colorPrimary: colour,
        colorAccent: accent,
        colorHeader: header,
      });
      setDone('Saved. Clients see it the next time they open the portal.');
      await load();
      window.dispatchEvent(new Event(BRANDING_CHANGED));
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file: File): Promise<void> => {
    setError(null);
    setDone(null);
    if (!TYPES.includes(file.type)) {
      setError('The logo must be a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > 256 * 1024) {
      setError('The logo must be 256 KB or smaller.');
      return;
    }
    setBusy(true);
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      let binary = '';
      for (const b of bytes) binary += String.fromCharCode(b);
      await api.put('/api/tenant/portal-branding/logo', { contentType: file.type, data: btoa(binary) });
      setDone('Logo uploaded.');
      await load();
      window.dispatchEvent(new Event(BRANDING_CHANGED));
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const removeLogo = async (): Promise<void> => {
    setBusy(true);
    try {
      await api.del('/api/tenant/portal-branding/logo');
      setDone('Logo removed.');
      await load();
      window.dispatchEvent(new Event(BRANDING_CHANGED));
    } catch (err) {
      setError(failure(err));
    } finally {
      setBusy(false);
    }
  };

  const locked = !state.canChange || busy;

  return (
    <Section
      title="Client portal"
      summary="What your clients see when they sign in at /portal/. A client sees only the sites you grant them in Users."
    >
      <div className="form-narrow">
        {error !== null && <Banner tone="fault">{error}</Banner>}
        {done !== null && <Banner tone="ok">{done}</Banner>}
        {!state.canChange && <Banner tone="info">Only an owner or admin can change these.</Banner>}

        <Field id="portal-name" label="Name shown to clients" hint="In the portal's header and the browser tab. Blank shows COSMON.">
          <input id="portal-name" value={name} disabled={locked} maxLength={60} onChange={(e) => setName(e.target.value)} />
        </Field>

        <Field id="portal-logo" label="Logo" hint="PNG, JPEG or WebP, up to 256 KB. Shown beside the name.">
          <div className="row-tight">
            {state.logoUrl !== null && <img src={state.logoUrl} alt="Current logo" style={{ height: 32, width: 'auto' }} />}
            <input
              id="portal-logo"
              type="file"
              accept={TYPES.join(',')}
              disabled={locked}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f !== undefined) void upload(f);
                e.target.value = '';
              }}
            />
            {state.logoUrl !== null && (
              <Button size="sm" variant="ghost" disabled={locked} onClick={() => void removeLogo()}>
                Remove logo
              </Button>
            )}
          </div>
        </Field>

        <ColourField
          id="portal-colour"
          label="Brand colour"
          hint="Buttons and highlights. The portal keeps text readable whatever you pick, and lifts a very dark colour on its dark theme so buttons stay visible."
          value={colour}
          fallback="#3c4a5a"
          disabled={locked}
          note={colour === (state.colorPrimary ?? '') ? readabilityNote(state.readability.primary) : null}
          onChange={setColour}
        />

        <ColourField
          id="portal-accent"
          label="Accent colour"
          hint="Links and the support contact. Blank uses the brand colour."
          value={accent}
          fallback="#3c4a5a"
          disabled={locked}
          note={accent === (state.colorAccent ?? '') ? readabilityNote(state.readability.accent) : null}
          onChange={setAccent}
        />

        <ColourField
          id="portal-header"
          label="Header colour"
          hint="The bar across the top, in both light and dark. Blank uses the portal’s own surface. White keeps a dark logo visible at night."
          value={header}
          fallback="#ffffff"
          disabled={locked}
          note={null}
          onChange={setHeader}
        />

        <Field id="portal-email" label="Support email" hint="Shown to clients as who to contact when something looks wrong.">
          <input id="portal-email" type="email" value={email} disabled={locked} onChange={(e) => setEmail(e.target.value)} />
        </Field>

        <Field id="portal-phone" label="Support phone">
          <input id="portal-phone" value={phone} disabled={locked} maxLength={40} onChange={(e) => setPhone(e.target.value)} />
        </Field>

        <Button disabled={locked} onClick={() => void save()}>
          Save
        </Button>
      </div>
    </Section>
  );
}
